(() => {
  class CollectionStyleSlider {
    constructor(section) {
      this.section = section;

      this.viewport = section.querySelector(
        '[data-collection-slider-viewport]'
      );

      this.track = section.querySelector(
        '[data-collection-slider-track]'
      );

      this.slides = [
        ...section.querySelectorAll(
          '[data-collection-slider-slide]'
        )
      ];

      this.prevButton = section.querySelector(
        '[data-collection-slider-prev]'
      );

      this.nextButton = section.querySelector(
        '[data-collection-slider-next]'
      );

      if (
        !this.viewport ||
        !this.track ||
        !this.slides.length
      ) {
        return;
      }

      /* -----------------------------------------------
         Slider state
      ----------------------------------------------- */

      this.currentTranslate = 0;

      this.startX = 0;
      this.currentX = 0;
      this.startTranslate = 0;

      this.isDragging = false;

      /*
        IMPORTANT:
        This is only true when the user actually
        moved the slider.
      */
      this.hasMoved = false;

      /*
        Used to prevent the click generated after
        a real drag/swipe.
      */
      this.suppressNextClick = false;

      this.maxTranslate = 0;
      this.slideStep = 0;

      this.pointerId = null;

      this.resizeTimer = null;

      this.init();
    }


    /* =====================================================
       INIT
    ===================================================== */

    init() {
      this.calculate();

      this.setTranslate(
        0,
        false
      );

      this.bindEvents();

      this.updateButtons();
    }


    /* =====================================================
       CALCULATE
    ===================================================== */

    calculate() {
      const firstSlide =
        this.slides[0];

      if (!firstSlide) {
        return;
      }

      const slideWidth =
        firstSlide.getBoundingClientRect().width;

      const trackStyles =
        window.getComputedStyle(
          this.track
        );

      const gap =
        parseFloat(
          trackStyles.columnGap ||
          trackStyles.gap ||
          0
        ) || 0;

      this.slideStep =
        slideWidth + gap;

      const trackWidth =
        this.track.scrollWidth;

      const viewportWidth =
        this.viewport.clientWidth;

      this.maxTranslate =
        Math.max(
          0,
          trackWidth - viewportWidth
        );

      /*
        Keep current position inside
        valid slider boundaries.
      */

      this.currentTranslate =
        Math.max(
          -this.maxTranslate,
          Math.min(
            0,
            this.currentTranslate
          )
        );
    }


    /* =====================================================
       GET CURRENT TRANSLATE
    ===================================================== */

    getCurrentTranslate() {
      const transform =
        window.getComputedStyle(
          this.track
        ).transform;

      if (
        !transform ||
        transform === 'none'
      ) {
        return 0;
      }

      const match =
        transform.match(
          /matrix.*\((.+)\)/
        );

      if (!match) {
        return 0;
      }

      const values =
        match[1].split(',');

      return (
        parseFloat(values[4]) || 0
      );
    }


    /* =====================================================
       SET TRANSLATE
    ===================================================== */

    setTranslate(
      value,
      animate = true
    ) {
      const clamped =
        Math.max(
          -this.maxTranslate,
          Math.min(
            0,
            value
          )
        );

      this.currentTranslate =
        clamped;

      if (!animate) {
        this.track.style.transition =
          'none';
      } else {
        this.track.style.transition = '';
      }

      this.track.style.transform =
        `translate3d(${clamped}px, 0, 0)`;

      if (!animate) {
        requestAnimationFrame(() => {
          this.track.style.transition = '';
        });
      }
    }


    /* =====================================================
       MAX STEP
    ===================================================== */

    getMaxStep() {
      if (
        this.slideStep <= 0 ||
        this.maxTranslate <= 0
      ) {
        return 0;
      }

      return Math.ceil(
        this.maxTranslate /
        this.slideStep
      );
    }


    /* =====================================================
       NEXT
    ===================================================== */

    next() {
      if (
        this.maxTranslate <= 0
      ) {
        return;
      }

      const nextPosition =
        this.currentTranslate -
        this.slideStep;

      /*
        If next position reaches the end,
        go exactly to the end.
      */

      if (
        Math.abs(nextPosition) >=
        this.maxTranslate
      ) {
        this.setTranslate(
          -this.maxTranslate,
          true
        );
      } else {
        this.setTranslate(
          nextPosition,
          true
        );
      }

      this.updateButtons();
    }


    /* =====================================================
       PREVIOUS
    ===================================================== */

    previous() {
      if (
        this.currentTranslate >= 0
      ) {
        return;
      }

      const previousPosition =
        this.currentTranslate +
        this.slideStep;

      if (
        previousPosition >= 0
      ) {
        this.setTranslate(
          0,
          true
        );
      } else {
        this.setTranslate(
          previousPosition,
          true
        );
      }

      this.updateButtons();
    }


    /* =====================================================
       BUTTON STATES
    ===================================================== */

    updateButtons() {
      const atStart =
        this.currentTranslate >= -1;

      const atEnd =
        this.maxTranslate <= 1 ||
        Math.abs(
          this.currentTranslate
        ) >=
        this.maxTranslate - 1;

      /* Previous */

      if (this.prevButton) {
        this.prevButton.classList.toggle(
          'is-hidden',
          atStart
        );

        this.prevButton.disabled =
          atStart;
      }

      /* Next */

      if (this.nextButton) {
        this.nextButton.classList.toggle(
          'is-hidden',
          atEnd
        );

        this.nextButton.disabled =
          atEnd;
      }
    }


    /* =====================================================
       POINTER DOWN
    ===================================================== */

    pointerDown(event) {
      /*
        Ignore right mouse button.
      */

      if (
        event.pointerType === 'mouse' &&
        event.button !== 0
      ) {
        return;
      }

      /*
        Ignore arrows.
      */

      if (
        event.target.closest(
          '[data-collection-slider-prev], [data-collection-slider-next]'
        )
      ) {
        return;
      }

      this.isDragging = true;

      this.hasMoved = false;

      this.startX =
        event.clientX;

      this.currentX =
        event.clientX;

      this.startTranslate =
        this.currentTranslate;

      this.viewport.classList.add(
        'is-dragging'
      );

      /*
        IMPORTANT:

        We DO NOT call preventDefault()
        here.

        This allows normal <a> clicks.
      */

      /*
        Pointer capture is intentionally
        not used here.

        This makes normal anchor clicks
        more reliable across browsers.
      */
    }


    /* =====================================================
       POINTER MOVE
    ===================================================== */

    pointerMove(event) {
      if (!this.isDragging) {
        return;
      }

      this.currentX =
        event.clientX;

      const difference =
        this.currentX -
        this.startX;

      /*
        Only consider this a drag after
        meaningful movement.
      */

      if (
        Math.abs(difference) > 8
      ) {
        this.hasMoved = true;
      }

      /*
        Normal click:

        difference <= 8

        DO NOTHING.

        This is extremely important because
        collection <a> should open normally.
      */

      if (!this.hasMoved) {
        return;
      }

      /*
        Actual drag.

        Now prevent browser native behavior.
      */

      event.preventDefault();

      this.setTranslate(
        this.startTranslate +
        difference,
        false
      );
    }


    /* =====================================================
       POINTER UP
    ===================================================== */

    pointerUp(event) {
      if (!this.isDragging) {
        return;
      }

      this.isDragging = false;

      this.viewport.classList.remove(
        'is-dragging'
      );

      const difference =
        this.currentX -
        this.startX;

      const absoluteDifference =
        Math.abs(difference);

      const threshold =
        Math.min(
          60,
          this.viewport.clientWidth *
          0.12
        );

      /*
        ---------------------------------------------
        NORMAL CLICK
        ---------------------------------------------

        If user simply clicked a collection
        and did not drag, do NOT touch the
        click event.
      */

      if (
        absoluteDifference <= 8
      ) {
        this.hasMoved = false;

        /*
          Normal browser click continues.
          
          <a href="collection-url">
          
          will open the collection.
        */

        return;
      }


      /*
        ---------------------------------------------
        ACTUAL DRAG / SWIPE
        ---------------------------------------------
      */

      this.hasMoved = true;


      /*
        Swipe left
      */

      if (
        absoluteDifference >
        threshold
      ) {
        if (difference < 0) {
          this.next();
        } else {
          this.previous();
        }
      } else {
        /*
          Small drag.

          Snap to nearest slide.
        */

        this.snapToNearest();
      }


      /*
        The browser may generate a click
        immediately after pointerup.

        Mark it so the click handler can
        cancel only this accidental click.
      */

      this.suppressNextClick = true;


      /*
        Reset after browser finishes the
        current click event.
      */

      setTimeout(() => {
        this.suppressNextClick = false;
        this.hasMoved = false;
      }, 0);
    }


    /* =====================================================
       POINTER CANCEL
    ===================================================== */

    pointerCancel() {
      if (!this.isDragging) {
        return;
      }

      this.isDragging = false;

      this.viewport.classList.remove(
        'is-dragging'
      );

      this.suppressNextClick = true;

      this.snapToNearest();

      setTimeout(() => {
        this.suppressNextClick = false;
        this.hasMoved = false;
      }, 0);
    }


    /* =====================================================
       SNAP
    ===================================================== */

    snapToNearest() {
      if (
        this.slideStep <= 0
      ) {
        return;
      }

      const current =
        Math.abs(
          this.currentTranslate
        );

      let index =
        Math.round(
          current /
          this.slideStep
        );

      const maxIndex =
        this.getMaxStep();

      index =
        Math.min(
          index,
          maxIndex
        );

      let target =
        -(index * this.slideStep);

      /*
        Exact last position.
      */

      if (
        index >= maxIndex
      ) {
        target =
          -this.maxTranslate;
      }

      this.setTranslate(
        target,
        true
      );

      this.updateButtons();
    }


    /* =====================================================
       COLLECTION CLICK
       
       IMPORTANT:
       Normal collection clicks are NEVER blocked.
    ===================================================== */

    handleCollectionClick(event) {
      /*
        If this is an accidental click generated
        after dragging/swiping, stop it.
      */

      if (
        this.suppressNextClick
      ) {
        event.preventDefault();
        event.stopPropagation();

        this.suppressNextClick =
          false;

        return;
      }

      /*
        NORMAL COLLECTION CLICK

        Do absolutely nothing.

        Browser will follow:
        
        <a href="{{ selected_collection.url }}">
      */

    }


    /* =====================================================
       EVENTS
    ===================================================== */

    bindEvents() {

      /* -----------------------------------------------
         PREVIOUS
      ----------------------------------------------- */

      if (this.prevButton) {
        this.prevButton.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();

            this.previous();
          }
        );
      }


      /* -----------------------------------------------
         NEXT
      ----------------------------------------------- */

      if (this.nextButton) {
        this.nextButton.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();

            this.next();
          }
        );
      }


      /* -----------------------------------------------
         POINTER DOWN
      ----------------------------------------------- */

      this.viewport.addEventListener(
        'pointerdown',
        (event) => {
          this.pointerDown(event);
        }
      );


      /* -----------------------------------------------
         POINTER MOVE
      ----------------------------------------------- */

      this.viewport.addEventListener(
        'pointermove',
        (event) => {
          this.pointerMove(event);
        },
        {
          passive: false
        }
      );


      /* -----------------------------------------------
         POINTER UP
      ----------------------------------------------- */

      this.viewport.addEventListener(
        'pointerup',
        (event) => {
          this.pointerUp(event);
        }
      );


      /* -----------------------------------------------
         POINTER CANCEL
      ----------------------------------------------- */

      this.viewport.addEventListener(
        'pointercancel',
        () => {
          this.pointerCancel();
        }
      );


      /* -----------------------------------------------
         COLLECTION CARD CLICK

         IMPORTANT:

         This listener does NOT block normal
         collection clicks.
      ----------------------------------------------- */

      this.viewport.addEventListener(
        'click',
        (event) => {
          this.handleCollectionClick(
            event
          );
        },
        false
      );


      /* -----------------------------------------------
         KEYBOARD
      ----------------------------------------------- */

      this.section.addEventListener(
        'keydown',
        (event) => {

          const target =
            event.target;

          /*
            Don't interfere with form elements.
          */

          if (
            target &&
            (
              target.matches('input') ||
              target.matches('textarea') ||
              target.matches('select') ||
              target.isContentEditable
            )
          ) {
            return;
          }


          /* Arrow Right */

          if (
            event.key === 'ArrowRight'
          ) {
            event.preventDefault();

            this.next();
          }


          /* Arrow Left */

          if (
            event.key === 'ArrowLeft'
          ) {
            event.preventDefault();

            this.previous();
          }
        }
      );


      /* -----------------------------------------------
         PREVENT IMAGE DRAG
      ----------------------------------------------- */

      this.section
        .querySelectorAll('img')
        .forEach((image) => {

          image.addEventListener(
            'dragstart',
            (event) => {
              event.preventDefault();
            }
          );

        });


      /* -----------------------------------------------
         RESIZE
      ----------------------------------------------- */

      window.addEventListener(
        'resize',
        () => {

          clearTimeout(
            this.resizeTimer
          );

          this.resizeTimer =
            setTimeout(() => {
              this.handleResize();
            }, 100);

        },
        {
          passive: true
        }
      );


      /* -----------------------------------------------
         WINDOW LOAD
      ----------------------------------------------- */

      window.addEventListener(
        'load',
        () => {
          this.handleResize();
        },
        {
          once: true
        }
      );
    }


    /* =====================================================
       RESIZE HANDLER
    ===================================================== */

    handleResize() {
      const oldTranslate =
        this.currentTranslate;

      this.calculate();

      const newTranslate =
        Math.max(
          -this.maxTranslate,
          Math.min(
            0,
            oldTranslate
          )
        );

      this.setTranslate(
        newTranslate,
        false
      );

      this.updateButtons();
    }
  }


  /* =======================================================
     INITIALIZE
  ======================================================= */

  function initCollectionStyleSlider(
    root = document
  ) {

    const sections =
      root.querySelectorAll(
        '[data-collection-style-slider]'
      );

    sections.forEach(
      (section) => {

        /*
          Don't initialize same section twice.
        */

        if (
          section.__collectionStyleSlider
        ) {
          return;
        }

        section.__collectionStyleSlider =
          new CollectionStyleSlider(
            section
          );

      }
    );
  }


  /* =======================================================
     DOM READY
  ======================================================= */

  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initCollectionStyleSlider();
      }
    );

  } else {

    initCollectionStyleSlider();

  }


  /* =======================================================
     SHOPIFY THEME EDITOR
  ======================================================= */

  document.addEventListener(
    'shopify:section:load',
    (event) => {

      initCollectionStyleSlider(
        event.target
      );

    }
  );


  /* =======================================================
     SHOPIFY SECTION UNLOAD
  ======================================================= */

  document.addEventListener(
    'shopify:section:unload',
    (event) => {

      const section =
        event.target.querySelector?.(
          '[data-collection-style-slider]'
        );

      if (
        section &&
        section.__collectionStyleSlider
      ) {

        section.__collectionStyleSlider =
          null;

      }

    }
  );

})();